// Placeholder data for the Formulary Discover page.
//
// TEMPORARY — every export here mirrors the EXACT response shape of an endpoint
// that does not exist yet. See services/DISCOVER_DATA_CONTRACTS.md for the
// per-dataset contracts (the ### numbers below refer to its sections).
//
// The seam is the service layer: each method in services/{discover,medication,
// reference}.js returns one of these instead of `new Request(...).send()`, with
// the real call sitting commented directly above it. Swapping to live endpoints
// is therefore per-method and local — uncomment the request, drop the fixture
// return. No store or component code is aware the data is local, because it
// consumes the same shape either way.
//
// Delete this file once all five endpoints are live.

// ### 1 — GET /facilities -> { facilities: string[] }
export const FACILITY_NAMES = ['Hospital C', 'Hospital C East', 'Hospital D', 'Hospital D North'];

// ### 2 — patient populations are NOT fixtured. They are a fixed two-value set
// with no endpoint behind them, so they live in
// modules/formulary/constants/patientPopulations.js as a real constant.

// ### 3 — GET /medications -> { medications: string[] }
// Uppercase, matching `network_medication_name` in the source data. The chart
// UPPERCASEs for display and lowercases again when routing, so casing here is
// what the rest of the pipeline assumes.
export const ALL_MEDICATIONS = [
  'ACETAMINOPHEN',
  'AMIODARONE',
  'ARGATROBAN',
  'BIVALIRUDIN',
  'CISATRACURIUM',
  'DEXMEDETOMIDINE',
  'DILTIAZEM',
  'DIAZEPAM',
  'DOBUTAMINE',
  'DOPAMINE',
  'EPINEPHRINE',
  'ESMOLOL',
  'FENTANYL',
  'FUROSEMIDE',
  'HEPARIN',
  'HYDROMORPHONE',
  'INSULIN',
  'KETAMINE',
  'LABETALOL',
  'LIDOCAINE',
  'LORAZEPAM',
  'MIDAZOLAM',
  'MILRINONE',
  'MORPHINE',
  'NICARDIPINE',
  'NITROGLYCERIN',
  'NOREPINEPHRINE',
  'OXYTOCIN',
  'PANTOPRAZOLE',
  'PHENYLEPHRINE',
  'PROPOFOL',
  'REMIFENTANIL',
  'ROCURONIUM',
  'VANCOMYCIN',
  'VASOPRESSIN',
];

// ### 4 — GET /medication-categories -> { medicationCategories: Category[] }
// `medications[]` is what the chart's `therapy` filter expands to. Title-cased
// here to match the real endpoint's payload; consumers compare case-insensitively.
export const MEDICATION_CATEGORIES = [
  {
    name: 'Controlled Substances',
    description: 'Regulated medications with potential for abuse or dependence',
    medications: [
      'Diazepam',
      'Fentanyl',
      'Hydromorphone',
      'Ketamine',
      'Lorazepam',
      'Midazolam',
      'Morphine',
      'Remifentanil',
    ],
  },
  {
    name: 'Vasopressors',
    description: 'Agents that raise blood pressure by constricting blood vessels',
    medications: ['Dopamine', 'Epinephrine', 'Norepinephrine', 'Phenylephrine', 'Vasopressin'],
  },
  {
    name: 'Sedatives',
    description: 'Agents used for procedural and continuous sedation',
    medications: ['Dexmedetomidine', 'Ketamine', 'Lorazepam', 'Midazolam', 'Propofol'],
  },
  {
    name: 'Anticoagulants',
    description: 'Agents that inhibit clot formation',
    medications: ['Argatroban', 'Bivalirudin', 'Heparin'],
  },
  {
    name: 'Antiarrhythmics',
    description: 'Agents used to treat irregular heart rhythms',
    medications: ['Amiodarone', 'Diltiazem', 'Esmolol', 'Lidocaine'],
  },
  {
    name: 'Neuromuscular Blockers',
    description: 'Agents causing skeletal muscle paralysis',
    medications: ['Cisatracurium', 'Rocuronium'],
  },
];

// ### 8 — POST /chart-data { metrics, filters, limit }
//        -> { results: [{ name, metrics: { <metric>: number } }] }
//
// The four metrics have incomparable units, so each gets its own generator
// rather than one shared random number — the chart's height normalization and
// `normalizeMax: 100` on waste only look right if the magnitudes are realistic:
//
//   most_skus         COUNT DISTINCT formulations      small integer (1-9)
//   most_wasted       wasted/dispensed * 100           percent (0-100)
//   most_utilized     SUM(containers)                  count (hundreds-thousands)
//   most_bag_changes  median containers/day            small decimal (0.5-6)
//
// Values are derived from a hash of the medication name, so they are stable
// across reloads (no flicker on refetch) and differ per medication.
function hash(text) {
  let value = 0;
  for (let i = 0; i < text.length; i += 1) {
    value = (value * 31 + text.charCodeAt(i)) % 100000;
  }
  return value;
}

const METRIC_GENERATORS = {
  most_skus: (seed) => 1 + (seed % 9),
  // One decimal, 0.5-24.4 — well under the 100 ceiling, so bars stay short and
  // the full-height-hit-area behavior stays exercised.
  most_wasted: (seed) => Math.round((0.5 + (seed % 240) / 10) * 10) / 10,
  most_utilized: (seed) => 120 + (seed % 3800),
  most_bag_changes: (seed) => Math.round((0.5 + (seed % 55) / 10) * 10) / 10,
};

// The seed prefix the per-medication value hash is salted with.
//
// Facility, patient population and the date range cannot SELECT rows in a
// dataset that has one row per medication, so they shift the values instead:
// every distinct combination produces a different — but stable and repeatable —
// set of numbers. Reselecting a previous combination reproduces its exact
// chart, which is what makes the fixture usable for eyeballing a filter change.
//
// Facility and population are SORTED before joining, so picking A then B and
// picking B then A agree. The date range contributes its span in days rather
// than its endpoints: a 30-day window and a 180-day window differ, while
// sliding a window of the same length shifts it less dramatically — mirroring
// how the real metrics respond more to how much data is in range than to
// exactly where the range sits.
//
// Seeded PER METRIC, not once per request: each metric keeps the value series
// it had when metrics were fetched one at a time, so selecting a metric shows
// the same numbers whether or not other metrics are selected alongside it.
function seedFor(metric, filters) {
  const parts = [metric];

  const facility = [...(filters.facility || [])].sort();
  if (facility.length) parts.push(`f=${facility.join('|')}`);

  const population = [...(filters.patientPopulation || [])].sort();
  if (population.length) parts.push(`p=${population.join('|')}`);

  // Dates are YYYY-MM-DD, so Date.parse is safe and the difference is exact.
  if (filters.dateFilterApplied && filters.startDate && filters.endDate) {
    const start = Date.parse(filters.startDate);
    const end = Date.parse(filters.endDate);
    if (!Number.isNaN(start) && !Number.isNaN(end)) {
      parts.push(`d=${Math.round((end - start) / 86400000)}`);
    }
  }

  return parts.join(':');
}

// The medications matching `filters`, before any metric values are attached.
//
// These are the filters that decide WHICH medications appear — therapy (### 4),
// inclusions, exclusions, minInfusions. The rest (facility, patientPopulation,
// date range) have no dimension to filter ON here, since there is one row per
// medication rather than one per medication-per-facility-per-day, so they
// participate in the value SEED instead. That is not what the real endpoint
// will do (it aggregates over the rows those filters actually select), but it
// produces the property that matters for exercising the page: changing any
// control visibly changes the chart, deterministically, without inventing a
// fake multi-dimensional dataset. See `seedFor`.
function selectMedications(filters) {
  const upper = (list) => (list || []).map((item) => String(item).toUpperCase());

  let names = [...ALL_MEDICATIONS];

  // `therapy` holds category NAMES; expand them to medication names.
  //
  // Categories come from the CALLER (`filters.categories`), not from the module
  // constant: Discover sends presets plus the user's custom categories from
  // localStorage, and reading the constant here would expand only the six
  // presets — selecting a custom category would silently match nothing and
  // empty the chart. Falls back to the presets when the caller sends none.
  const therapy = filters.therapy || [];
  if (therapy.length) {
    const catalog = filters.categories && filters.categories.length ? filters.categories : MEDICATION_CATEGORIES;
    const expanded = new Set(
      catalog
        .filter((category) => therapy.includes(category.name))
        .flatMap((category) => upper(category.medications || []))
    );
    names = names.filter((name) => expanded.has(name));
  }

  const inclusions = upper(filters.inclusions);
  if (inclusions.length) {
    names = names.filter((name) => inclusions.includes(name));
  }

  const exclusions = upper(filters.exclusions);
  if (exclusions.length) {
    names = names.filter((name) => !exclusions.includes(name));
  }

  // `minInfusions` is the "in use" floor: a formulation whose utilization falls
  // below it is not considered stocked, so the medication drops out entirely.
  //
  // Compared against the medication's UTILIZATION, not against any requested
  // metric — the floor means the same thing regardless of what is being charted,
  // so the same medications drop out whichever metrics are selected. That figure
  // is computed off the same seed as the values, so raising the floor while a
  // facility or date range is selected filters the numbers actually on screen.
  //
  // Utilization spans 120-3920, so the catalog default of 5 excludes nothing —
  // correct, since the default is meant to be a no-op floor that only rules out
  // formulations with almost no use.
  const minInfusions = Number.parseInt(filters.minInfusions, 10);
  if (!Number.isNaN(minInfusions) && minInfusions > 1) {
    const utilizationSeed = seedFor('most_utilized', filters);
    names = names.filter((name) => METRIC_GENERATORS.most_utilized(hash(`${utilizationSeed}:${name}`)) >= minInfusions);
  }

  return names;
}

// Builds the pivoted chart rows for ALL requested metrics in one pass.
//
// Mirrors what the real `POST /chart-data` must do, so the store can treat the
// fixture and the endpoint identically:
//
//   1. select the medications the filters allow (`selectMedications`)
//   2. attach every requested metric to each one, as a `metrics` object
//   3. sort by the requested metrics IN ORDER, descending, ties broken by name
//   4. slice to `limit`
//
// Steps 3 and 4 belong to the server now: it is the side that can push the
// top-N into SQL instead of shipping the whole catalog for the client to trim.
// Unknown metric keys are skipped rather than emitted as NaN, so a stale
// selection cannot poison a row.
export function buildChartRows(metrics, filters, limit) {
  const requested = (metrics || []).filter((metric) => METRIC_GENERATORS[metric]);
  if (!requested.length) return [];

  const applied = filters || {};
  const seeds = requested.map((metric) => [metric, seedFor(metric, applied)]);

  const rows = selectMedications(applied).map((name) => {
    const values = {};
    seeds.forEach(([metric, seed]) => {
      values[metric] = METRIC_GENERATORS[metric](hash(`${seed}:${name}`));
    });
    return { name, metrics: values };
  });

  rows.sort((a, b) => {
    for (let i = 0; i < requested.length; i += 1) {
      const diff = (b.metrics[requested[i]] || 0) - (a.metrics[requested[i]] || 0);
      if (diff !== 0) return diff;
    }
    return a.name.localeCompare(b.name);
  });

  const max = Number.parseInt(limit, 10);
  return !Number.isNaN(max) && max > 0 ? rows.slice(0, max) : rows;
}
